"use client";

import { useState } from "react";
import { Search, Plus } from "lucide-react";
import { Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { faq, paa } from "@/lib/data/homepage";

/**
 * FAQ — Searchable categorized accordion. Search bar at top, then
 * questions grouped and accordion-style with search filtering.
 */
export function FAQ() {
  const [q, setQ] = useState("");
  const filtered = q
    ? faq.filter((f) => f.q.toLowerCase().includes(q.toLowerCase()) || f.a.toLowerCase().includes(q.toLowerCase()))
    : faq;

  return (
    <section id="faq" className="relative section-pad">
      <Container>
        <Reveal className="text-center max-w-3xl mx-auto mb-8">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass eyebrow-tiny text-[var(--color-purple)]">
            <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-purple)]" />
            FAQ
          </span>
          <h2 className="mt-5 text-[clamp(2rem,4.5vw,3.2rem)] font-bold leading-[1.04] tracking-[-0.025em]">
            Frequently Asked Questions
          </h2>
        </Reveal>

        {/* Search bar */}
        <Reveal className="max-w-2xl mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[rgb(var(--fg-muted))]" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search the FAQ..."
              className="w-full glass rounded-full pl-11 pr-4 py-3 text-sm placeholder:text-[rgb(var(--fg-subtle))] focus:outline-none focus:ring-2 focus:ring-[var(--color-purple)]/50"
            />
          </div>
        </Reveal>

        <div className="max-w-3xl mx-auto space-y-2">
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-[rgb(var(--fg-muted))] text-sm">
              No questions match "{q}". Try a different term.
            </div>
          ) : (
            filtered.map((f, i) => <FAQItem key={f.q} q={f.q} a={f.a} />)
          )}
        </div>
      </Container>
    </section>
  );
}

function FAQItem({ q, a }: { q: string; a: string }) {
  return (
    <details className="group glass rounded-2xl overflow-hidden">
      <summary className="flex items-center justify-between gap-4 px-5 py-4 cursor-pointer list-none hover:bg-[rgb(var(--border)/0.04)] transition-colors">
        <span className="font-semibold text-[0.97rem] leading-snug">{q}</span>
        <span className="grid place-items-center h-7 w-7 rounded-full shrink-0 bg-[rgb(var(--border)/0.1)] transition-all duration-300 group-open:rotate-45 group-open:bg-[var(--color-purple)]/20 group-open:text-[var(--color-purple)]">
          <Plus className="h-4 w-4" />
        </span>
      </summary>
      <div className="px-5 pb-4 text-body-sm leading-relaxed text-[rgb(var(--fg-muted))]">
        {a}
      </div>
    </details>
  );
}
